import {useState, useEffect, useMemo} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useLoader, useGlobalConfigState} from '_context'
import {BasketModel, OrderAddressModel, PaymentInstrumentModel} from '_types'
import {useWishlistActions} from '_customHooks/wishlist'
import {useMinicart, useCurrentBasket} from '_customHooks'

interface AddItemToBasketProps {
    productId: string
    quantity: number
    // einstein addToCart event props
    price?: number
}

interface addTaxesForBasketProps {
    [key: string]: {
        taxItems: {
            id: string
            rate: number
            value: number
        }[]
    }
}

interface UpdateBasketProps {
    currency?: string
    c_PP_API_TransactionID?: string
    c_alreadyConfirmed?: boolean
    c_is_guest?: boolean
    c_paypalPaymentMethod?: string
    c_siteId?: string
}

interface UpdateBasketCustomerProps {
    email: string
}

/**
 * @name useBasket
 *
 * @hook
 *
 * @description
 * Custom hook to manage basket.
 *
 * @remarks
 * When adding new basket mutations to this hook, always include the `c_locale` query parameter
 * in the mutation's parameters object. This is required to ensure that the mutation and
 * the basket query from useCurrentBasket share the same cache key.
 * If `c_locale` is omitted from a mutation, the cache will not update correctly and
 * components using the basket may show stale basket data after a mutation.
 *
 * @param {boolean} shouldShowLoader - Indicates if loader must be shown or not.
 *
 * @returns An object containing basket data and functions to manage basket items.
 */
const useBasket = (shouldShowLoader: boolean = true) => {
    const {locale} = useMultiSite()
    const {previewMinicart} = useMinicart()
    const einstein = useEinstein()
    const {appPreferences} = useGlobalConfigState()
    const {CountriesWithZeroPrices} = appPreferences

    const aliasCountry = useMemo(() => locale.alias?.split('-')[1].toLowerCase(), [locale])
    const hasHiddenPrices = useMemo(
        () => (Array.isArray(CountriesWithZeroPrices) ? CountriesWithZeroPrices.find((country: string) => country.toLowerCase() === aliasCountry) : undefined),
        [CountriesWithZeroPrices, aliasCountry],
    )

    const {data: basket, refetch: refetchBasket, isLoading: isLoadingBasket} = useCurrentBasket()

    const {removeItemFromWishlist} = useWishlistActions()

    const {mutateAsync: createBasketMutation} = useShopperBasketsMutation('createBasket')
    const {mutateAsync: updateItemInBasketMutation} = useShopperBasketsMutation('updateItemInBasket')
    const {mutateAsync: removeItemFromBasketMutation} = useShopperBasketsMutation('removeItemFromBasket')
    const {mutateAsync: addItemToBasketMutation} = useShopperBasketsMutation('addItemToBasket')
    const {mutateAsync: updateBasketMutation} = useShopperBasketsMutation('updateBasket')
    const {mutateAsync: addTaxesForBasketMutation} = useShopperBasketsMutation('addTaxesForBasket')
    const {mutateAsync: updateShippingAddressMutation} = useShopperBasketsMutation('updateShippingAddressForShipment')
    const {mutateAsync: updateShippingMethodMutation} = useShopperBasketsMutation('updateShippingMethodForShipment')
    const {mutateAsync: addPaymentInstrumentMutation} = useShopperBasketsMutation('addPaymentInstrumentToBasket')
    const {mutateAsync: updateCustomerMutation} = useShopperBasketsMutation('updateCustomerForBasket')
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation('removePaymentInstrumentFromBasket')
    // see @remarks when adding new basket mutations

    const [basketProductsCount, setBasketProductsCount] = useState<number>(0)
    const {showLoader, hideLoader} = useLoader()

    const createBasket = async () => {
        await createBasketMutation({
            parameters: {c_locale: locale?.id},
            body: {},
        })
    }

    const updateItemInBasket = async (basketItemId: string, quantity: number) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateItemInBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    itemId: basketItemId,
                    c_locale: locale?.id,
                },
                body: {
                    quantity: quantity,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const removeItemFromBasket = async (basketItemId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await removeItemFromBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    itemId: basketItemId,
                    c_locale: locale?.id,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addItemToBasket = async (...products: Array<AddItemToBasketProps>) => {
        if (shouldShowLoader) showLoader()

        try {
            if (!hasHiddenPrices) {
                await addItemToBasketMutation({
                    parameters: {
                        basketId: basket?.basketId || '',
                        c_locale: locale?.id,
                    },
                    body: products.map(({productId, quantity}) => ({
                        productId,
                        quantity,
                    })),
                })

                einstein.sendAddToCart(
                    products.map(({productId, quantity, price}) => ({
                        productId,
                        quantity,
                        price,
                    })),
                )
            }
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
            previewMinicart()
        }
    }

    const updateBasket = async (basketUpdates: UpdateBasketProps) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: basketUpdates,
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addTaxesForBasket = async (taxes: addTaxesForBasketProps) => {
        await addTaxesForBasketMutation({
            parameters: {
                basketId: basket?.basketId || '',
                c_locale: locale?.id,
            },
            body: {
                taxes: taxes,
            },
        })
    }

    const updateBasketShippingAddress = async (shippingAddress: OrderAddressModel, useAsBilling: boolean): Promise<BasketModel> => {
        if (shouldShowLoader) showLoader()

        try {
            const response = await updateShippingAddressMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    shipmentId: 'me',
                    useAsBilling,
                    c_locale: locale?.id,
                },
                body: shippingAddress || {},
            })
            return response as BasketModel
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const updateBasketShippingMethod = async (shippingMethodId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateShippingMethodMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    shipmentId: 'me',
                    c_locale: locale?.id,
                },
                body: {
                    id: shippingMethodId,
                },
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addPaymentInstrumentToBasket = async (paymentInstrument: PaymentInstrumentModel) => {
        if (shouldShowLoader) showLoader()

        try {
            await addPaymentInstrumentMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: paymentInstrument || {},
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const updateBasketCustomer = async (customerUpdates: UpdateBasketCustomerProps) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateCustomerMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: customerUpdates,
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const removePaymentInstrument = async (paymentInstrumentId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await removePaymentInstrumentFromBasket({
                parameters: {
                    basketId: basket?.basketId || '',
                    paymentInstrumentId: paymentInstrumentId,
                    c_locale: locale?.id,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    /**
     * Handles the buy products button behaviour in wishlist page.
     *
     * @description
     * All the products that have a selected size are moved into the basket and removed from the wishlist.
     *
     * @remarks
     * Variant ID has the priority over product ID for handle all buy products business logic.
     *
     * @param {Array<{productId: string, quantity: number, variantId?: string}>} productItems
     * @returns {Promise<void>} A promise that resolves when action is completed.
     */
    const addAllWishlistProductsToCart = async (
        productItems: Array<{productId: string; quantity: number; variantId?: string; price?: number}>,
    ): Promise<void> => {
        async function removeItemsSequentially(productItems: Array<{productId: string}>) {
            await productItems.reduce(async (previousPromise, item) => {
                await previousPromise
                return removeItemFromWishlist(item.productId)
            }, Promise.resolve())
        }

        try {
            if (shouldShowLoader) showLoader()

            await addItemToBasket(
                ...productItems.map((item) => ({
                    productId: item.variantId || item.productId,
                    quantity: item.quantity,
                    price: item.price,
                })),
            )

            await removeItemsSequentially(productItems)
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    useEffect(() => {
        if (!basket && !isLoadingBasket) {
            // create basket if no basket exists
            void createBasket()
        } else if (!isLoadingBasket) {
            // update the basket currency if needed
            if (locale && basket.currency !== locale.preferredCurrency) {
                void updateBasket({currency: locale.preferredCurrency})
            }
        }
    }, [isLoadingBasket])

    useEffect(() => {
        if (basket) {
            setBasketProductsCount(basket?.c_productsQuantity || 0)
        }
    }, [basket])

    return {
        basket,
        removeItemFromBasket,
        updateItemInBasket,
        addItemToBasket,
        refetchBasket: refetchBasket as unknown as () => Promise<BasketModel>,
        basketProductsCount,
        updateBasket,
        addTaxesForBasket,
        updateBasketShippingAddress,
        updateBasketShippingMethod,
        addPaymentInstrumentToBasket,
        updateBasketCustomer,
        addAllWishlistProductsToCart,
        removePaymentInstrument,
    }
}

export {useBasket}
